import PropTypes from 'prop-types';
import React from 'react';

import {assert} from 'Asserts';
import {isSchemaTag, SchemaTag, Tag} from 'folder-items/FileConstants';
import BenchLink from 'link-preview/BenchLink';
import ComputedStateTableCell from 'schemas/tags/computed/ComputedStateTableCell';
import * as ComputedTagUtil from 'schemas/tags/computed/ComputedTagUtil';
import {getLinksKey} from 'schemas/tags/TagUtil';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import * as ChipConstants from 'ui-library/chips/ChipConstants';
import ChipList, {BaseChipList} from 'ui-library/chips/ChipList';
import * as ChipUtil from 'ui-library/chips/ChipUtil';
import DateTableCell from 'ui-library/scrollable/table-cells/DateTableCell';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import TableCellWithTooltip, {
  NO_VALUE_TOOLTIP_TITLE,
} from 'ui-library/scrollable/table-cells/TableCellWithTooltip';
import TextValue from 'ui-library/TextValue';
import UnitAwareScrollableTableCellTagValue from 'units/UnitAwareScrollableTableCellTagValue';

interface SimpleTagValueProps {
  tag: {value?: string | null; url?: string | null; taggedItemName?: string; isFiller?: boolean};
  className?: string;
}

export function SimpleTagValue(props: SimpleTagValueProps): JSX.Element {
  const tagValueText = props.tag.value || NO_VALUE_TOOLTIP_TITLE;
  const optionalTaggedItemNameSuffix = props.tag.taggedItemName ? ` (${props.tag.taggedItemName})` : '';
  const tooltipTitle = `${tagValueText}${optionalTaggedItemNameSuffix}`;

  return (
    <TableCellWithTooltip className={props.className} tooltipTitle={tooltipTitle}>
      {(() => {
        if (props.tag.value && props.tag.url) {
          return <BenchLink href={props.tag.url}>{props.tag.value}</BenchLink>;
        } else {
          return <TextValue value={props.tag.value} isFaded={props.tag.isFiller} />;
        }
      })()}
    </TableCellWithTooltip>
  );
}

export type TagForTableDisplay = Tag & {taggedItemName?: string; isFiller?: boolean};
export type SchemaTagForTableDisplay = SchemaTag & TagForTableDisplay;

interface TagValueProps {
  tag?: TagForTableDisplay;
  className?: string;
  maxChipsPerRow?: number;
}

/*
 * Render values for multiple multi-link field values in one cell
 * Displays at most one chip per set of field values (aka per element in the row)
 */
export function CollapsedChipsFieldValue({
  fieldValues,
  maxChipsPerRow,
  collapsedMultiLinkOverflowElement,
}: {
  fieldValues: Array<TagForTableDisplay>;
  maxChipsPerRow?: number;
  collapsedMultiLinkOverflowElement?: (numLinks: number) => JSX.Element;
}): JSX.Element | null {
  assert(fieldValues.length > 1, 'CollapsedChipsFieldValue should only be passed multi-element list');
  assert(
    fieldValues.every((val) => ChipUtil.tagShouldRenderChips(val)),
    'Field value should be chip field values'
  );

  const schemaFieldValues = fieldValues as Array<SchemaTagForTableDisplay>;
  if (schemaFieldValues[0].targetBlobs) {
    const targetBlobs = schemaFieldValues.flatMap((val) =>
      val.targetBlobs!.length ? val.targetBlobs![0] : []
    );
    const overflowTargetBlobs = schemaFieldValues.flatMap((val) => val.targetBlobs!.slice(1));
    return (
      <BaseChipList
        maxChips={Math.min(fieldValues.length, maxChipsPerRow ?? ChipConstants.CHIP_DISPLAY_LIMIT_TABLE_ROW)}
        chips={[...targetBlobs, ...overflowTargetBlobs].map((blob) => (
          <Chip size={ChipSize.SMALL} text={blob.name} href={blob.url} />
        ))}
        chipSize={ChipSize.SMALL}
        viewAllOverflowLink={
          collapsedMultiLinkOverflowElement &&
          collapsedMultiLinkOverflowElement([...targetBlobs, ...overflowTargetBlobs].length)
        }
      />
    );
  } else {
    const linksKey = getLinksKey(schemaFieldValues[0].tagSchemaField.fieldType);
    const linkedItems = schemaFieldValues.flatMap((val) => {
      const links = val[linksKey]! as Array<{
        id: string;
      }>;
      return links.length ? links[0] : [];
    });
    const overflowLinkedItems = schemaFieldValues.flatMap((val) =>
      (
        val[linksKey]! as Array<{
          id: string;
        }>
      ).slice(1)
    );
    return (
      <ChipList
        maxChips={Math.min(fieldValues.length, maxChipsPerRow ?? ChipConstants.CHIP_DISPLAY_LIMIT_TABLE_ROW)}
        itemIds={[...linkedItems, ...overflowLinkedItems].map((item) => item.id)}
        chipSize={ChipSize.SMALL}
        viewAllOverflowLink={
          collapsedMultiLinkOverflowElement &&
          collapsedMultiLinkOverflowElement([...linkedItems, ...overflowLinkedItems].length)
        }
      />
    );
  }
}

/**
 * Renders a tag's value, for use within ScrollableTable cells.
 */
export default function TagValue({tag, className, maxChipsPerRow}: TagValueProps): JSX.Element | null {
  if (!tag) {
    return null;
  }

  if (isSchemaTag(tag)) {
    if (ComputedTagUtil.shouldShowComputedState(tag)) {
      return <ComputedStateTableCell tag={tag} />;
    } else if (tag.dateValue) {
      return <DateTableCell className={className} date={tag.dateValue} />;
    } else if (tag.datetimeValue) {
      return <DatetimeTableCell className={className} datetime={tag.datetimeValue} showTimeInCell={true} />;
    } else if (ChipUtil.tagShouldRenderChips(tag)) {
      if (tag.targetBlobs) {
        return (
          <BaseChipList
            maxChips={maxChipsPerRow ?? ChipConstants.CHIP_DISPLAY_LIMIT_TABLE_ROW}
            chips={tag.targetBlobs.map((blob) => (
              <Chip size={ChipSize.SMALL} text={blob.name} href={blob.url} />
            ))}
            chipSize={ChipSize.SMALL}
          />
        );
      } else {
        const linkedItems = tag[getLinksKey(tag.tagSchemaField.fieldType)] as Array<{id: string}>;
        return (
          <ChipList
            maxChips={maxChipsPerRow ?? ChipConstants.CHIP_DISPLAY_LIMIT_TABLE_ROW}
            itemIds={linkedItems.map((item) => item.id)}
            chipSize={ChipSize.SMALL}
          />
        );
      }
    } else if (tag.tagSchemaField.unitSymbol != null) {
      return <UnitAwareScrollableTableCellTagValue className={className} tag={tag} />;
    }
  }
  return <SimpleTagValue className={className} tag={tag} />;
}

TagValue.propTypes = {
  tag: PropTypes.object,
  className: PropTypes.string,
};
